import { FC, useMemo, useState } from 'react';
import { Grid, IconButton } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../../../models/Redux';
import { useStyles } from './styles';
import StreamingButton from './parts/StreamingButton';

import userDarkIcon from '../../../../assets/streaming/actions/dark/user-dark.svg';
import openModeIcon from '../../../../assets/streaming/actions/dark/ic-openMode-dark.svg';
import openModeOffIcon from '../../../../assets/streaming/actions/dark/ic-openMode-dark-off.svg';
import stopTurnIcon from '../../../../assets/streaming/actions/dark/hangCall-dark.png';
import raiseHandIcon from '../../../../assets/streaming/actions/dark/raise-hand.svg';
import raiseHandOffIcon from '../../../../assets/streaming/actions/dark/raise-hand-off.svg';
import microIcon from '../../../../assets/streaming/actions/dark/ic-streaming-mic-dark.svg';
import microOffIcon from '../../../../assets/streaming/actions/dark/ic-streaming-mic-off-dark.svg';
import cameraIcon from '../../../../assets/streaming/actions/dark/ic-camera-on.svg';
import cameraOffIcon from '../../../../assets/streaming/actions/dark/ic-camera-off-dark.svg';
import arrowIcon from '../../../../assets/streaming/actions/dark/down-arrow.svg';
import chatIcon from '../../../../assets/streaming/actions/dark/chat-bubble-dark.svg';
import chatOffIcon from '../../../../assets/streaming/actions/dark/chat-bubble-dark-off.svg';
import settingsIcon from '../../../../assets/streaming/actions/dark/settings-dark.svg';
import shareScreenOn from '../../../../assets/streaming/actions/dark/share-screen-on.png';
import shareScreenOff from '../../../../assets/streaming/actions/dark/share-screen-off.png';

import {
    SET_LOCAL_STREAMING_STATE,
    thunkAllowViewerTurn,
    thunkToggleChat,
    thunkToggleOpenMicEvent,
    thunkToggleRaiseHand,
    thunkToggleRaiseHandEvent,
} from '../../../../store/actions/Streaming.actions';
import { useResponsiveSize } from '../../../../config/hooks/useResponsiveSize';
import { DeviceSensitive, UIBreakPoint } from '../../config';
import useScreenShareToggle from '../../hooks/useScreenShareToggle/useScreenShareToggle';

interface ButtonManagerProps {
    mobileLayout?: boolean;
}

const ButtonManager: FC<ButtonManagerProps> = ({ mobileLayout }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobileUI = useResponsiveSize(UIBreakPoint, DeviceSensitive);

    const {
        user: { user_ide: userIde, user_username: userUsername },
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);
    const {
        eventData: {
            user: { user_ide: influencerUserIde },
        },
        localStreamingState,
        streamingFlags,
        streamingState: { current_state: currentState },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isAdmin = useMemo(() => {
        return !!currentState?.admin_users?.find((username: string) => username === userUsername);
    }, [currentState, userUsername]);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const [isOpen, setIsOpen] = useState<boolean>(!isMobileUI);
    const [isSharing, toggleScreenShare] = useScreenShareToggle(localStreamingState.room, () => {});

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const muteCamera = (active?: boolean) => {
        if (localStreamingState.room) {
            try {
                localStreamingState.room.localParticipant.videoTracks.forEach(track => {
                    track.track.enable(!localStreamingState.camera);
                });
                dispatch({
                    type: SET_LOCAL_STREAMING_STATE,
                    payload: {
                        ...localStreamingState,
                        camera: active || !localStreamingState.camera,
                    },
                });
            } catch (error) {
                console.error('Error onSetMicrophone:', error);
            }
        }
    };

    const muteMicrophone = (active?: boolean) => {
        if (localStreamingState.room) {
            try {
                localStreamingState.room.localParticipant.audioTracks.forEach(track => {
                    track.track.enable(!localStreamingState.microphone);
                });
                dispatch({
                    type: SET_LOCAL_STREAMING_STATE,
                    payload: {
                        ...localStreamingState,
                        microphone: active || !localStreamingState.microphone,
                    },
                });
            } catch (error) {
                console.error('Error onSetMicrophone:', error);
            }
        }
    };

    const openSettingsModal = () => {
        dispatch({
            type: SET_LOCAL_STREAMING_STATE,
            payload: {
                ...localStreamingState,
                settingsModal: true,
            },
        });
    };

    const shareScreen = () => {
        toggleScreenShare();
    };

    const openParticipantList = (open?: boolean) => {
        dispatch({
            type: SET_LOCAL_STREAMING_STATE,
            payload: {
                ...localStreamingState,
                showParticipantList: !!open,
            },
        });
    };

    const toggleRaiseHandMode = (active?: boolean) => {
        dispatch(thunkToggleRaiseHandEvent(active || !currentState?.raised_hand_enabled));
    };

    const toggleOpenMode = (active?: boolean) => {
        dispatch(thunkToggleOpenMicEvent(active || !currentState?.open_mic));
    };

    const toggleChat = (active?: boolean) => {
        dispatch(thunkToggleChat(active || !currentState?.chat_enabled));
    };

    const stopViewerTurn = () => {
        if (currentState?.speaking_viewer?.username) {
            dispatch(thunkAllowViewerTurn(currentState?.speaking_viewer?.username));
        }
    };

    const raiseHand = () => {
        dispatch(thunkToggleRaiseHand());
    };

    const renderButtonManager = () => {
        return (
            isEventInfluencer ||
            isAdmin ||
            currentState?.open_mic ||
            streamingFlags.hasTurn ||
            currentState?.raised_hand_enabled
        );
    };

    const renderButtonsInfluencer = () => {
        return (
            <>
                {streamingFlags.started && (
                    <>
                        <StreamingButton
                            icon={userDarkIcon}
                            altText={t('Viewers')}
                            title={t('Viewers')}
                            onClick={() => openParticipantList(!localStreamingState.showParticipantList)}
                        />
                        <StreamingButton
                            icon={currentState?.open_mic ? openModeIcon : openModeOffIcon}
                            altText={t('Open mode icon')}
                            title={t('Open Mode')}
                            onClick={() => toggleOpenMode()}
                        />
                        <StreamingButton
                            icon={currentState?.raised_hand_enabled ? raiseHandIcon : raiseHandOffIcon}
                            altText={t('Raise Hand icon')}
                            title={t('Raise Hand')}
                            onClick={() => toggleRaiseHandMode()}
                        />
                        {currentState?.speaking_viewer && (
                            <StreamingButton
                                icon={stopTurnIcon}
                                altText={t('Stop viewer turn icon')}
                                title={t('Stop viewer turn')}
                                onClick={() => stopViewerTurn()}
                            />
                        )}
                        <StreamingButton
                            icon={currentState?.chat_enabled ? chatIcon : chatOffIcon}
                            altText={t('Enable/disable chat icon')}
                            title={t('Chat')}
                            onClick={() => toggleChat()}
                        />
                        <StreamingButton
                            icon={localStreamingState.camera ? cameraIcon : cameraOffIcon}
                            altText={t('Camera off icon')}
                            title={t('Camera On/Off')}
                            onClick={() => muteCamera()}
                        />
                        <StreamingButton
                            icon={localStreamingState.microphone ? microIcon : microOffIcon}
                            altText={t('Microphone On/Off icon')}
                            title={t('Microphone On/Off')}
                            onClick={() => muteMicrophone()}
                        />
                    </>
                )}
                {!isMobileUI && (
                    <>
                        {streamingFlags.started && (
                            <StreamingButton
                                icon={isSharing ? shareScreenOff : shareScreenOn}
                                altText={isSharing ? t('Stop Sharing') : t('Share Screen')}
                                title={isSharing ? t('Stop Sharing') : t('Share Screen')}
                                onClick={() => shareScreen()}
                            />
                        )}
                        <StreamingButton
                            icon={settingsIcon}
                            altText={t('Settings icon')}
                            title={t('Settings')}
                            onClick={() => openSettingsModal()}
                        />
                    </>
                )}
            </>
        );
    };

    const renderButtonsViewer = () => {
        return (
            <>
                {isAdmin && streamingFlags.started && (
                    <StreamingButton
                        icon={userDarkIcon}
                        altText={t('Viewers')}
                        title={t('Viewers')}
                        onClick={() => openParticipantList(!localStreamingState.showParticipantList)}
                    />
                )}
                {currentState?.raised_hand_enabled && !streamingFlags.hasTurn && (
                    <StreamingButton
                        icon={localStreamingState.raiseHand ? raiseHandIcon : raiseHandOffIcon}
                        altText={t('Raise Hand icon')}
                        title={t('Raise Hand')}
                        onClick={() => raiseHand()}
                    />
                )}
                {streamingFlags.hasTurn && streamingFlags.started && (
                    <StreamingButton
                        icon={localStreamingState.camera ? cameraIcon : cameraOffIcon}
                        altText={t('Camera off icon')}
                        title={t('Camera On/Off')}
                        onClick={() => muteCamera()}
                    />
                )}
                {(streamingFlags.hasTurn || currentState?.open_mic) && streamingFlags.started && (
                    <StreamingButton
                        icon={localStreamingState.microphone ? microIcon : microOffIcon}
                        altText={t('Microphone On/Off icon')}
                        title={t('Microphone On/Off')}
                        onClick={() => muteMicrophone()}
                    />
                )}
                {(streamingFlags.hasTurn || currentState?.open_mic) && streamingFlags.started && (
                    <StreamingButton
                        icon={settingsIcon}
                        altText={t('Settings icon')}
                        title={t('Settings')}
                        onClick={() => openSettingsModal()}
                    />
                )}
            </>
        );
    };

    const renderButtons = () => {
        if (!isOpen) return null;
        return isEventInfluencer ? renderButtonsInfluencer() : renderButtonsViewer();
    };

    if (!renderButtonManager()) {
        return null;
    }

    let arrowUp = isOpen;

    if (!isMobileUI) {
        arrowUp = !arrowUp;
    }

    return (
        <Grid
            container
            xs={12}
            justify="center"
            alignContent="center"
            alignItems="center"
            direction="row"
            className={classes.baseContainer}
        >
            <Grid
                container
                item
                xs={12}
                justify="center"
                alignContent="center"
                alignItems={mobileLayout ? 'flex-end' : 'flex-start'}
                direction="row"
                className={clsx(
                    classes.container,
                    isMobileUI ? classes.mobileContainer : classes.desktopContainer,
                    isOpen ? classes.containerPaddingRight : '',
                )}
            >
                <IconButton
                    onClick={toggleMenu}
                    className={clsx(classes.toggleMenu, arrowUp ? classes.reverse : '')}
                >
                    <img
                        className={clsx(classes.toggleMenuIcon)}
                        src={arrowIcon}
                        alt={t('Toggle button menu')}
                    />
                </IconButton>
                {renderButtons()}
            </Grid>
        </Grid>
    );
};

export default ButtonManager;
